import React, { useState, useEffect } from 'react';
import { X, History, Building2, Phone, FileText, Crown, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { Conversation } from '../../stores/whatsapp/chatStore';
import { formatPhone, getConnectionLabel } from '../../services/whatsapp/waFormatters';
import { PRIORITIES } from '../../services/whatsapp/waConstants';
import { useKanbanStore } from '../../stores/whatsapp/kanbanStore';
import waApi from '../../services/whatsapp/waApi';
import { api } from '../../services/api';

interface ClientInfoPanelProps {
  conversation: Conversation;
  onClose: () => void;
}

// Dados do cadastro do contato no Indicadores (issue #29) — buscados à parte da
// conversa, só quando o contato é salvo. Campos que a conversa não carrega.
interface ContactDetails {
  cargo?: string;
  departamento?: string;
  empresa_nome?: string;
  principal?: boolean;
  observacoes?: string;
  avaliacao_desativada?: boolean;
}

const PRIORITY_TAGS: Record<string, { label: string; color: string }> = {
  vip: { label: 'VIP', color: 'bg-yellow-100 text-yellow-700' },
  premium: { label: 'Premium', color: 'bg-purple-100 text-purple-700' },
  high: { label: 'Alta prioridade', color: 'bg-red-100 text-red-700' },
  normal: { label: 'Normal', color: 'bg-gray-100 text-gray-600' },
};

export default function ClientInfoPanel({ conversation, onClose }: ClientInfoPanelProps) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState(conversation.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);

  // Atualizar notas ao trocar de conversa
  useEffect(() => {
    setNotes(conversation.notes || '');
  }, [conversation.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await waApi.put(`/conversations/${conversation.id}`, { notes });
      toast.success('Observações salvas');
    } catch {
      toast.error('Erro ao salvar observações');
    } finally {
      setSavingNotes(false);
    }
  };
  const kanbanTicket = useKanbanStore(s => s.getByConversation(conversation.id));
  const priorityConfig = PRIORITIES[conversation.priority];

  // Contato não salvo: client_id igual ao telefone (ou só dígitos) indica que
  // não há vínculo com o Indicadores — nesse caso não há contato para editar,
  // então só abrimos a lista. Mesma regra usada no banner do ChatWindow.
  const isContactSaved =
    !!conversation.client_id &&
    conversation.client_id !== conversation.client_phone &&
    !/^\d{10,}$/.test(conversation.client_id);

  // Dados do cadastro do contato (issue #29): cargo, empresa, departamento,
  // contato principal e observações. Buscados pelo telefone só quando o contato
  // é salvo no Indicadores; o registro é localizado casando o id da conversa.
  const [contactDetails, setContactDetails] = useState<ContactDetails | null>(null);

  useEffect(() => {
    setContactDetails(null);
    if (!isContactSaved) return undefined;

    let active = true;
    const phone = (conversation.client_phone || '').replace(/[^0-9]/g, '');
    api
      .get('/api/v1/empresas/contatos/todos/', { params: { page: 1, size: 50, search: phone } })
      .then(res => {
        const items = res.data?.data?.items || [];
        const found = items.find((c: any) => String(c.id) === String(conversation.client_id));
        if (active && found) {
          setContactDetails({
            cargo: found.cargo,
            departamento: found.departamento,
            empresa_nome: found.empresa_nome,
            principal: found.principal,
            observacoes: found.observacoes,
            avaliacao_desativada: found.avaliacao_desativada,
          });
        }
      })
      .catch(() => {
        // Falha silenciosa: o painel segue exibindo o que a conversa já traz.
      });
    return () => {
      active = false;
    };
  }, [conversation.id, conversation.client_id, conversation.client_phone, isContactSaved]);

  const handleEditContact = () => {
    // Propaga id + telefone para a página de clientes abrir direto o modal de
    // edição do contato (a busca por telefone localiza o registro mesmo fora da
    // primeira página da lista). Sem vínculo, abre só a lista para cadastro.
    let url = '/whatsapp/clientes';
    if (isContactSaved) {
      const phone = (conversation.client_phone || '').replace(/[^0-9]/g, '');
      const params = new URLSearchParams({ edit: conversation.client_id });
      if (phone) params.set('phone', phone);
      url = `/whatsapp/clientes?${params.toString()}`;
    }
    window.open(url, '_blank');
  };

  // Departamento atual do atendimento (nome legível, ex.: "Suporte", "Fiscal").
  // O backend grava o departamento já resolvido; usa o atribuído quando existir.
  const departmentLabel = conversation.assigned_department || conversation.department || '';

  // Conexão WhatsApp usada na conversa. Mesma regra do cabeçalho do chat
  // (issue #40): label da conexão → departamento (contato salvo) → "WhatsApp
  // Principal". Sempre retorna um texto.
  const connectionLabel = getConnectionLabel(conversation);

  // Situação do atendimento (issue #30): mesma semântica do OwnershipBanner da
  // tela principal — encerrado; em atendimento (com atendente); ou aguardando na
  // fila (sem atendente). Usa assigned_agent_id (mesma chave do banner) para os
  // dois nunca divergirem.
  const attendanceState = (() => {
    if (conversation.status === 'closed') {
      return { label: 'Encerrado', classes: 'bg-gray-100 text-gray-600' };
    }
    if (conversation.assigned_agent_id) {
      return { label: 'Em atendimento', classes: 'bg-blue-100 text-blue-700' };
    }
    return { label: 'Aguardando na fila', classes: 'bg-orange-100 text-orange-700' };
  })();

  // Tags derivadas da conversa
  const tags: { label: string; color: string }[] = [];
  const clientTag = PRIORITY_TAGS[conversation.client_priority] || PRIORITY_TAGS.normal;
  tags.push(clientTag);
  if (conversation.subject) {
    tags.push({ label: conversation.subject, color: 'bg-blue-100 text-blue-700' });
  }
  if (kanbanTicket) {
    tags.push({ label: `Ticket: ${kanbanTicket.columnId}`, color: 'bg-green-100 text-green-700' });
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-white/80 backdrop-blur-xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100/60 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">Informações do Cliente</h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100/80 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Avatar and name */}
      <div className="flex flex-col items-center border-b border-gray-100/60 px-4 py-8">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-wa-400 to-wa-600 text-2xl font-semibold text-white shadow-wa-apple-md">
          {conversation.client_name.charAt(0).toUpperCase()}
        </div>
        <h4 className="mt-4 text-center text-base font-semibold tracking-tight text-gray-900">
          {conversation.client_name}
        </h4>
        <div className="mt-1 flex items-center gap-1.5 text-gray-400">
          <Phone className="h-3.5 w-3.5" />
          <span className="text-sm">{formatPhone(conversation.client_phone)}</span>
        </div>
        {/* Nivel do cliente */}
        <div className="mt-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${clientTag.color}`}
          >
            <Crown size={11} />
            {clientTag.label}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="border-b border-gray-100/60">
        <div className="px-4 py-4">
          <h5 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Informações
          </h5>
          <div className="space-y-3">
            {conversation.client_company && (
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100/80">
                  <Building2 className="h-4 w-4 text-gray-400" />
                </div>
                <span className="text-sm text-gray-700">{conversation.client_company}</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100/80">
                <Phone className="h-4 w-4 text-gray-400" />
              </div>
              <span className="text-sm text-gray-700">
                {formatPhone(conversation.client_phone)}
              </span>
            </div>
            {conversation.subject && (
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100/80">
                  <FileText className="h-4 w-4 text-gray-400" />
                </div>
                <span className="text-sm text-gray-700">{conversation.subject}</span>
              </div>
            )}
            <div className="flex items-center gap-3 pl-11">
              <span className="text-xs text-gray-400">Status:</span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${attendanceState.classes}`}
              >
                {attendanceState.label}
              </span>
            </div>
            {conversation.assigned_agent_name && (
              <div className="flex items-center gap-3 pl-11">
                <span className="text-xs text-gray-400">Atendente:</span>
                <span className="text-sm text-gray-700">{conversation.assigned_agent_name}</span>
              </div>
            )}
            {departmentLabel && (
              <div className="flex items-center gap-3 pl-11">
                <span className="text-xs text-gray-400">Departamento:</span>
                <span className="text-sm text-gray-700">{departmentLabel}</span>
              </div>
            )}
            {connectionLabel && (
              <div className="flex items-center gap-3 pl-11">
                <span className="text-xs text-gray-400">Conexão:</span>
                <span className="text-sm text-gray-700">{connectionLabel}</span>
              </div>
            )}
            {/* Avaliação ao finalizar (issue #42): só para contato salvo */}
            {contactDetails && (
              <div className="flex items-center gap-3 pl-11">
                <span className="text-xs text-gray-400">Avaliação:</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    contactDetails.avaliacao_desativada
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {contactDetails.avaliacao_desativada ? 'Desativada' : 'Ativada'}
                </span>
              </div>
            )}
            {/* Dados do cadastro do contato (issue #29) */}
            {!conversation.client_company && contactDetails?.empresa_nome && (
              <div className="flex items-center gap-3 pl-11">
                <span className="text-xs text-gray-400">Empresa:</span>
                <span className="text-sm text-gray-700">{contactDetails.empresa_nome}</span>
              </div>
            )}
            {contactDetails?.cargo && (
              <div className="flex items-center gap-3 pl-11">
                <span className="text-xs text-gray-400">Cargo:</span>
                <span className="text-sm text-gray-700">{contactDetails.cargo}</span>
              </div>
            )}
            {contactDetails?.principal && (
              <div className="flex items-center gap-3 pl-11">
                <span className="text-xs text-gray-400">Contato:</span>
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                  Principal da empresa
                </span>
              </div>
            )}
            {contactDetails?.observacoes && (
              <div className="flex gap-3 pl-11">
                <span className="flex-shrink-0 text-xs text-gray-400">Obs. do cadastro:</span>
                <span className="text-sm text-gray-700">{contactDetails.observacoes}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Priority */}
      <div className="border-b border-gray-100/60">
        <div className="px-4 py-4">
          <h5 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Prioridade
          </h5>
          {priorityConfig && (
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${priorityConfig.bg} ${priorityConfig.color}`}
            >
              {priorityConfig.label}
            </span>
          )}
        </div>
      </div>

      {/* Tags */}
      <div className="border-b border-gray-100/60">
        <div className="px-4 py-4">
          <h5 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Tags
          </h5>
          <div className="flex flex-wrap gap-1.5">
            {tags.map(tag => (
              <span
                key={tag.label}
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${tag.color}`}
              >
                {tag.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Acoes */}
      <div className="border-b border-gray-100/60">
        <div className="px-4 py-4">
          <h5 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Ações Rápidas
          </h5>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => {
                // Abre o histórico já filtrado por este cliente (issue #31). Usa o
                // telefone como termo de busca: é único e o filtro da página já
                // casa por telefone (funciona mesmo com contato não salvo).
                const phone = (conversation.client_phone || '').replace(/[^0-9]/g, '');
                navigate(phone ? `/whatsapp/historico?cliente=${phone}` : '/whatsapp/historico');
              }}
              className="flex w-full items-center gap-3 rounded-xl bg-gray-100/80 px-4 py-3 text-sm font-medium text-gray-600 transition-all duration-200 hover:bg-gray-200/80"
            >
              <History className="h-4 w-4" />
              Ver Histórico
            </button>
            <button
              type="button"
              onClick={handleEditContact}
              className="flex w-full items-center gap-3 rounded-xl bg-gray-100/80 px-4 py-3 text-sm font-medium text-gray-600 transition-all duration-200 hover:bg-gray-200/80"
            >
              <Pencil className="h-4 w-4" />
              Editar Contato
            </button>
          </div>
        </div>
      </div>

      {/* Kanban ticket info */}
      {kanbanTicket && (
        <div className="border-b border-gray-100/60">
          <div className="px-4 py-4">
            <h5 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Ticket Kanban
            </h5>
            <div className="space-y-1.5 rounded-xl bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">{kanbanTicket.title}</p>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="rounded-full bg-purple-100 px-2 py-0.5 font-medium text-purple-700">
                  {kanbanTicket.category}
                </span>
                <span>{kanbanTicket.columnId}</span>
              </div>
              {kanbanTicket.aiSummary && (
                <p className="mt-1 text-xs leading-relaxed text-gray-500">
                  {kanbanTicket.aiSummary.slice(0, 150)}...
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="px-4 py-4">
        <h5 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
          Observações
        </h5>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          onBlur={handleSaveNotes}
          disabled={savingNotes}
          placeholder="Adicionar observações sobre o cliente..."
          className="h-24 w-full resize-none rounded-xl border-none bg-gray-100/80 px-4 py-3 text-sm text-gray-700 outline-none transition-all placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-wa-500/30"
        />
      </div>
    </div>
  );
}
